import { supabase } from '../supabaseClient';
import { DEFAULT_THEME_KEY, normalizeThemeKey } from '../theme';

const defaultSettings = {
  viewMode: '3d',
  preferredColorIndex: 0,
  showSymbols: false,
  showReactions: true,
  chatSoundsEnabled: false,
  themeKey: DEFAULT_THEME_KEY,
};

// Get user settings from profile
export const getProfileSettings = async (username) => {
  console.log(`📖 Loading settings for user: ${username}`);
  const { data, error } = await supabase
    .from('profiles')
    .select('settings')
    .eq('username', username)
    .single();

  if (error) {
    console.warn('Could not fetch profile settings:', error.message);
    return defaultSettings;
  }

  const loaded = { ...defaultSettings, ...data?.settings };
  loaded.preferredColorIndex = parseInt(loaded.preferredColorIndex, 10) || 0;
  loaded.themeKey = normalizeThemeKey(loaded.themeKey);
  console.log(`📖 Loaded settings:`, loaded);
  return loaded;
};

// Save full settings object directly (no pre-read needed)
export const updateAllProfileSettings = async (username, settings) => {
  const { error } = await supabase
    .from('profiles')
    .update({ settings })
    .eq('username', username);

  if (error) {
    console.error('Could not update profile settings:', error.message);
    throw error;
  }
  return settings;
};

// Batch-load settings for multiple users in a single query
export const getProfileSettingsBatch = async (usernames) => {
  if (!usernames || usernames.length === 0) return {};

  const { data, error } = await supabase
    .from('profiles')
    .select('username, settings')
    .in('username', usernames);

  if (error) throw error;

  const result = {};
  (data || []).forEach(profile => {
    const settings = { ...defaultSettings, ...profile.settings };
    settings.preferredColorIndex = parseInt(settings.preferredColorIndex, 10) || 0;
    settings.themeKey = normalizeThemeKey(settings.themeKey);
    result[profile.username] = settings;
  });
  return result;
};

// Get a player's current leaderboard rank (1-based) and their rating
export const getPlayerRank = async (username) => {
  const { data: profile, error } = await supabase
    .from('profiles')
    .select('rating, games_played')
    .eq('username', username)
    .single();

  if (error || !profile || profile.games_played < 5) return null;

  const userRating = profile.rating || 1500;

  // Use GET (not HEAD) — Supabase PostgREST HEAD requests fail in some environments
  // (index.mjs:102 "Fetch failed loading: HEAD"). Fetching only 'username' is minimal.
  const { count } = await supabase
    .from('profiles')
    .select('username', { count: 'exact' })
    .gte('games_played', 5)
    .gt('rating', userRating);

  return { rank: (count || 0) + 1, rating: userRating };
};

export const getLeaderboardProfileSummary = async (username) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('games_played, rating')
    .eq('username', username)
    .single();

  if (error) throw error;
  return {
    games_played: data?.games_played || 0,
    rating: data?.rating || 1500,
  };
};

/**
 * Fetches full profile data for a user (for the profile stats modal).
 * PERF-3: Single RPC returns profile + rank in 1 query.
 * update_player_profile RPC confirmed deployed (functions.sql §6).
 */
export const getPlayerProfile = async (username) => {
  const { data, error } = await supabase.rpc('get_player_profile', { p_username: username });
  if (error) throw error;
  return {
    ...data,
    rating_history: Array.isArray(data?.rating_history) ? data.rating_history : [],
  };
};
